import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { LeaveEntry, LeaveType } from '@techgitter/contracts';
import { useLeaveManagement } from './hooks/useLeaveManagement';
import { useUsers } from '../../hooks/useUsers';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, DateField, TimeField } from '../dropdown';
import { makeTechnicianRenderItem } from './renderTechnicianOption';
import { buildUserColorIndex } from '../../utils/userColorIndex';

const LEAVE_TYPES: LeaveType[] = ['Annual', 'Sick', 'Other'];
const DEFAULT_TIME = '09:00';

/** Return the next business day (Mon–Fri) on or after tomorrow, as YYYY-MM-DD. */
function nextBusinessDay(from: Date = new Date()): string {
  const d = new Date(from);
  d.setDate(d.getDate() + 1);
  const day = d.getDay();
  if (day === 0) d.setDate(d.getDate() + 1); // Sunday → Monday
  if (day === 6) d.setDate(d.getDate() + 2); // Saturday → Monday
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toDateTimeLocal(isoStr: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toDateOnly(datetimeLocal: string): string {
  return datetimeLocal.split('T')[0] ?? '';
}

function isAllDayEntry(startAt: string, endAt: string): boolean {
  if (!startAt || !endAt) return true;
  const s = new Date(startAt);
  const e = new Date(endAt);
  return s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 23 && e.getMinutes() === 59;
}

interface AddLeavePanelProps {
  editingLeave: LeaveEntry | null;
  onClose: () => void;
  /** Called after a successful create/update/delete so the calendar can refresh. */
  onSaved?: () => void;
}

/**
 * Right-aligned Add/Edit Leave panel (replaces the former modal). Keeps the
 * leave form logic, validation, and API calls from the old LeaveDialog; only
 * the presentation moved to the shared SidePanel + FormField shell.
 */
export const AddLeavePanel: React.FC<AddLeavePanelProps> = ({
  editingLeave,
  onClose,
  onSaved,
}) => {
  const { isSubmitting, error, createLeave, updateLeave, deleteLeave } = useLeaveManagement();
  const { users: cachedUsers } = useUsers();

  const technicianNames = useMemo(() => {
    const names = new Set(cachedUsers.map(u => u.fullname));
    return Array.from(names).filter(Boolean).sort();
  }, [cachedUsers]);

  const renderTechnicianItem = useMemo(
    () => makeTechnicianRenderItem(buildUserColorIndex(cachedUsers)),
    [cachedUsers],
  );

  const [technicianName, setTechnicianName] = useState<string>('');
  const [startDate, setStartDate] = useState(''); // YYYY-MM-DD
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState(DEFAULT_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_TIME);
  const [allDay, setAllDay] = useState(true);
  const [leaveType, setLeaveType] = useState<LeaveType>('Annual');
  const [notes, setNotes] = useState('');

  // Populate when (re)opened or when the edited entry changes.
  useEffect(() => {
    if (editingLeave) {
      setTechnicianName(editingLeave.userFullName);
      const startDtl = toDateTimeLocal(editingLeave.startAt);
      const endDtl = toDateTimeLocal(editingLeave.endAt);
      setStartDate(toDateOnly(startDtl));
      setEndDate(toDateOnly(endDtl));
      const isAllDay = isAllDayEntry(editingLeave.startAt, editingLeave.endAt);
      setAllDay(isAllDay);
      if (isAllDay) {
        setStartTime(DEFAULT_TIME);
        setEndTime(DEFAULT_TIME);
      } else {
        setStartTime(startDtl.split('T')[1] ?? DEFAULT_TIME);
        setEndTime(endDtl.split('T')[1] ?? DEFAULT_TIME);
      }
      setLeaveType(editingLeave.leaveType);
      setNotes(editingLeave.notes ?? '');
    } else {
      const defaultDate = nextBusinessDay();
      setTechnicianName('');
      setStartDate(defaultDate);
      setEndDate(defaultDate);
      setStartTime(DEFAULT_TIME);
      setEndTime(DEFAULT_TIME);
      setAllDay(true);
      setLeaveType('Annual');
      setNotes('');
    }
  }, [editingLeave]);

  const handleSubmit = async () => {
    if (!technicianName.trim() || !startDate || !endDate) return;

    // For all-day leave, use explicit UTC boundaries so the date survives the
    // round-trip through Dataverse (which stores UTC); .toISOString() on local
    // midnight would shift the date in positive-UTC zones like AEST.
    const startIso = allDay
      ? `${startDate}T00:00:00Z`
      : new Date(`${startDate}T${startTime}`).toISOString();
    const endIso = allDay
      ? `${endDate}T23:59:00Z`
      : new Date(`${endDate}T${endTime}`).toISOString();

    if (editingLeave) {
      const success = await updateLeave(editingLeave.uuid, {
        startAt: startIso,
        endAt: endIso,
        leaveType,
        notes: notes.trim() || undefined,
      });
      if (success) {
        onSaved?.();
        onClose();
      }
    } else {
      const entry = await createLeave({
        userId: '', // Resolved by API via user lookup
        userFullName: technicianName.trim(),
        startAt: startIso,
        endAt: endIso,
        leaveType,
        notes: notes.trim() || undefined,
      });
      if (entry) {
        onSaved?.();
        onClose();
      }
    }
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!editingLeave) return;
    setIsDeleting(true);
    const success = await deleteLeave(editingLeave.uuid);
    setIsDeleting(false);
    setDeleteConfirmOpen(false);
    if (success) {
      onSaved?.();
      onClose();
    }
  };

  const isValid = !!(
    technicianName.trim() &&
    startDate &&
    endDate &&
    endDate >= startDate &&
    (allDay || startDate < endDate || endTime >= startTime)
  );

  return (
    <>
      <SidePanel
        title={editingLeave ? 'Edit Leave' : 'Add Leave'}
        type="leave"
        submitLabel={isSubmitting ? 'Saving…' : editingLeave ? 'Update' : 'Add Leave'}
        isValid={isValid && !isSubmitting}
        onClose={onClose}
        onSubmit={handleSubmit}
        onDelete={editingLeave ? () => setDeleteConfirmOpen(true) : undefined}
        deleteDisabled={isSubmitting || isDeleting}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        <SmartSelect
          label="Technician *"
          value={technicianName || null}
          options={technicianNames.map((t) => ({ value: t, label: t }))}
          onChange={(val) => setTechnicianName(val ?? '')}
          placeholder="Select technician…"
          renderItem={renderTechnicianItem}
          disabled={!!editingLeave}
        />

        <SmartSelect
          label="Leave Type"
          value={leaveType}
          options={LEAVE_TYPES.map((t) => ({ value: t, label: t }))}
          onChange={(val) => {
            if (val) setLeaveType(val as LeaveType);
          }}
        />

        <label className="side-panel__checkbox">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          All day
        </label>

        <div className="side-panel__field-row">
          <DateField
            label="Start Date *"
            value={startDate || null}
            onChange={(iso) => setStartDate(iso ?? '')}
          />
          <TimeField
            label="Start Time"
            value={startTime || null}
            onChange={(hhmm) => setStartTime(hhmm ?? '')}
            disabled={allDay}
          />
        </div>

        <div className="side-panel__field-row">
          <DateField
            label="End Date *"
            value={endDate || null}
            onChange={(iso) => setEndDate(iso ?? '')}
          />
          <TimeField
            label="End Time"
            value={endTime || null}
            onChange={(hhmm) => setEndTime(hhmm ?? '')}
            disabled={allDay}
          />
        </div>

        <FormField label="Notes (optional)">
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </SidePanel>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => !isDeleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Leave Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {leaveType.toLowerCase()} leave for {technicianName}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
